import StateObserver, { user } from 'src/StateObserver';
import { track } from 'src/lib/analytics/utils';
import {
  getAddFriendPopupViewCount,
  getProfileConsentPopupViewCount,
} from 'src/replicant/chatbot/chatbot.getters';
import type { LineProfileConsentResult } from '../profile/profile.api';
import type { IncentivizedOARewardClaimResult } from 'src/replicant/chatbot/chatbot.ruleset';

/**
 * Where the popup appeared in the session flow. SC onboarding uses the launch
 * surfaces (`splash_screen` first entry, `launch_sequence` every recurring
 * launch); in-game prompts use the high-intent surfaces (`post_tutorial`,
 * `level_5`, `level_15`, `level_complete`).
 */
export type ConsentSurface =
  | 'splash_screen'
  | 'launch_sequence'
  | 'post_tutorial'
  | 'level_5'
  | 'level_15'
  | 'level_complete';

/** Popup variants introduced by A/B 0489. */
export type IncentivizedOAPopupType =
  | 'incentive_half_modal'
  | 'incentive_popup';

/**
 * Emit ResubscribePopupOpen for a profile-consent popover and return the lifetime
 * view count to pair with the matching {@link trackProfileConsentClose}. Reuses
 * the resubscribe events with `popupType: 'profile_consent'`. The consent call
 * itself stays in the flow — these helpers only emit the bracketing analytics.
 */
export async function trackProfileConsentOpen(
  origin: ConsentSurface,
): Promise<number> {
  await StateObserver.invoke.incrementProfileConsentPopupViewCount();
  const allTimeViewCount = getProfileConsentPopupViewCount(user());

  track('ResubscribePopupOpen', {
    popupType: 'profile_consent',
    allTimeViewCount,
    isHintShown: false,
    origin,
  });

  return allTimeViewCount;
}

/**
 * Emit ResubscribePopupClose for a profile-consent popover (grant → `profile_grant`).
 * The SC consent popover can ALSO add the OA in one tap, so a grant alone doesn't
 * imply it — pass the post-grant OA subscription status as `oaSubscribed` for the
 * `didAddOAFriend` flag.
 */
export function trackProfileConsentClose(
  origin: ConsentSurface,
  outcome: LineProfileConsentResult,
  allTimeViewCount: number,
  oaSubscribed: boolean,
): void {
  const didGrantProfileConsent = outcome === 'granted';

  track('ResubscribePopupClose', {
    popupType: 'profile_consent',
    closeReason: didGrantProfileConsent ? 'profile_grant' : 'close_button',
    allTimeViewCount,
    isHintShown: false,
    origin,
    didGrantProfileConsent,
    didAddOAFriend: oaSubscribed,
  });
}

/**
 * Emit ResubscribePopupOpen for the OA Add-Friend overlay and return the lifetime
 * view count to pair with the matching {@link trackOfficialAccountFriendClose}.
 * Reuses the resubscribe events with `popupType: 'add_friend_chatbot'` — distinct
 * from the rich resubscribe surface's `direct_overlay` — tagged with `origin`.
 * The bare overlay has no hint, so `isHintShown: false`.
 */
export async function trackOfficialAccountFriendOpen(
  origin: ConsentSurface,
): Promise<number> {
  await StateObserver.invoke.incrementAddFriendPopupViewCount();
  const allTimeViewCount = getAddFriendPopupViewCount(user());

  track('ResubscribePopupOpen', {
    popupType: 'add_friend_chatbot',
    allTimeViewCount,
    isHintShown: false,
    origin,
  });

  return allTimeViewCount;
}

/**
 * Emit ResubscribePopupClose for the OA Add-Friend overlay (subscribed → `subscribe`).
 * Adding the OA does NOT grant the `profile` scope (the overlay only adds the
 * friend), so a subscribe sets `didAddOAFriend` only — never `didGrantProfileConsent`.
 */
export function trackOfficialAccountFriendClose(
  origin: ConsentSurface,
  subscribed: boolean,
  allTimeViewCount: number,
): void {
  track('ResubscribePopupClose', {
    popupType: 'add_friend_chatbot',
    closeReason: subscribed ? 'subscribe' : 'close_button',
    allTimeViewCount,
    isHintShown: false,
    origin,
    didGrantProfileConsent: false,
    didAddOAFriend: subscribed,
  });
}

/**
 * Emit the opening analytics for an A/B 0489 OA incentive surface.
 *
 * @param origin Session surface that rendered the incentive.
 * @param popupType Incentive composition that was rendered.
 * @returns The shared lifetime OA Add-Friend surface view count.
 */
export async function trackIncentivizedOAOpen(
  origin: ConsentSurface,
  popupType: IncentivizedOAPopupType,
): Promise<number> {
  // Reuse the OA Add-Friend counter to avoid another persistent state field.
  await StateObserver.invoke.incrementAddFriendPopupViewCount();
  const allTimeViewCount = getAddFriendPopupViewCount(user());

  // Record the rendered incentive surface.
  track('ResubscribePopupOpen', {
    popupType,
    allTimeViewCount,
    isHintShown: false,
    origin,
  });

  // Pair this count with the eventual close event.
  return allTimeViewCount;
}

/**
 * Emit closing analytics for an A/B 0489 OA incentive surface.
 *
 * @param origin Session surface that rendered the incentive.
 * @param popupType Incentive composition that was rendered.
 * @param result User close or client-verified reward claim outcome.
 * @param allTimeViewCount View count returned by {@link trackIncentivizedOAOpen}.
 */
export function trackIncentivizedOAClose(
  origin: ConsentSurface,
  popupType: IncentivizedOAPopupType,
  result: IncentivizedOARewardClaimResult | 'close_button',
  allTimeViewCount: number,
): void {
  // A grant proves friendship; already-claimed handles a rare stale client race.
  const didAddOAFriend = result === 'granted' || result === 'already_claimed';

  // Use the existing close vocabulary for successful and dismissed surfaces.
  const closeReason = didAddOAFriend ? 'subscribe' : 'close_button';

  // Record how the incentive surface completed.
  track('ResubscribePopupClose', {
    popupType,
    closeReason,
    result,
    allTimeViewCount,
    isHintShown: false,
    origin,
    didGrantProfileConsent: false,
    didAddOAFriend,
  });
}
